/**
 * Minimal structural shape of the address parts this helper compares. Anything carrying these
 * fields — Address, RealEstateOwned, SubjectProperty — can be passed without coupling to a
 * concrete model type.
 */
export interface AddressParts {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

/**
 * Normalize a free-text address part for tolerant comparison: trim, collapse internal
 * runs of whitespace to a single space, and lowercase.
 */
export function normalizeAddressPart(value: string | null | undefined): string {
  return (value ?? '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Normalize a ZIP for comparison by taking the leading ZIP-5, ignoring any "+4" suffix
 * or surrounding formatting.
 */
export function normalizeZip(value: string | null | undefined): string {
  const trimmed = (value ?? '').toString().trim();
  return trimmed.length >= 5 ? trimmed.substring(0, 5) : trimmed;
}

/**
 * True when `reo` refers to the same physical property as `address`.
 *
 * LLS-140: the online app links a co-borrower to the primary borrower's existing REO by
 * matching on address. The previous exact string comparison (raw case/whitespace, full
 * ZIP+4) missed whenever autocomplete/normalization reformatted the copied address, so a
 * second REO was created for the same property and exported to the LOS twice. Comparing
 * normalized parts prevents that miss.
 *
 * A blank street line never matches — otherwise two not-yet-entered addresses would
 * collapse onto each other.
 *
 * Pass `{ matchUnit: true }` only when BOTH compared shapes carry a unit (`address2`) — e.g.
 * REO-vs-REO or REO-vs-SubjectProperty. It keeps two units in one building distinct. Do NOT
 * enable it when one side is a plain `Address` (no `address2` field), or a real REO would fail
 * to match its own residency address.
 */
export function reoMatchesAddress(
  reo: AddressParts | null | undefined,
  address: AddressParts | null | undefined,
  options?: { matchUnit?: boolean }): boolean {
  const address1 = normalizeAddressPart(address?.address1);
  if (!address1) {
    return false;
  }

  if (normalizeAddressPart(reo?.address1) !== address1
    || normalizeAddressPart(reo?.city) !== normalizeAddressPart(address?.city)
    || normalizeAddressPart(reo?.state) !== normalizeAddressPart(address?.state)
    || normalizeZip(reo?.zipCode) !== normalizeZip(address?.zipCode)) {
    return false;
  }

  if (options?.matchUnit && normalizeAddressPart(reo?.address2) !== normalizeAddressPart(address?.address2)) {
    return false;
  }

  return true;
}
